import type { LocalDownloadFailureReport } from '@/lib/download-manager-local-failures';

/**
 * Runs downloads prepared in this browser (a sheet or markdown conversion)
 * for the download panel, which lives as long as the org app. The menu that
 * starts one can close right away (a context menu unmounts on select), so the
 * Preparing row, its timeout and its Retry belong here, not to the menu
 * (#6171 Live QA).
 */
export type LocalDownloadRun = {
  /** The row's identity: one row per file and format. */
  key: string;
  fileName: string;
  /** Prepares and saves the file; throws on failure. */
  work: () => Promise<void>;
  timeoutMs: number;
  /** Shown when the work hasn't settled by the deadline (retryable). */
  timeoutReason: string;
  describeFailure: (error: unknown) => { reason: string; retryable: boolean };
};

type RunnerDeps = {
  /** Shows the Preparing row; returns the attempt's token. */
  preparing: (row: { key: string; fileName: string }) => number;
  fail: (report: LocalDownloadFailureReport) => void;
  clear: (key: string, token: number) => void;
};

export function createLocalDownloadRunner(deps: RunnerDeps) {
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const releases = new Set<() => void>();

  /**
   * One attempt; Retry on its failed row runs another. It resolves when the
   * work settles or times out, whichever is first, so a hung attempt never
   * holds the row's Retry. Each attempt owns its row by token, so a late
   * result from a replaced attempt never touches the newer row, while a late
   * success still clears the attempt's own timed-out row.
   */
  const run = (job: LocalDownloadRun): Promise<void> => {
    const attempt = async (): Promise<void> => {
      const token = deps.preparing({ key: job.key, fileName: job.fileName });
      let release: () => void = () => {};
      const ended = new Promise<void>((resolve) => {
        release = resolve;
      });
      releases.add(release);
      const timer = setTimeout(() => {
        timers.delete(timer);
        deps.fail({
          key: job.key,
          fileName: job.fileName,
          reason: job.timeoutReason,
          retry: attempt,
          token,
        });
        release();
      }, job.timeoutMs);
      timers.add(timer);
      const work = (async () => {
        try {
          await job.work();
          deps.clear(job.key, token);
        } catch (error) {
          const failure = job.describeFailure(error);
          deps.fail({
            key: job.key,
            fileName: job.fileName,
            reason: failure.reason,
            retry: failure.retryable ? attempt : null,
            token,
          });
        } finally {
          clearTimeout(timer);
          timers.delete(timer);
          releases.delete(release);
          release();
        }
      })();
      await Promise.race([work, ended]);
    };
    return attempt();
  };

  /** The org app is going away: no timer outlives it, nothing waits. */
  const dispose = () => {
    for (const timer of timers) clearTimeout(timer);
    timers.clear();
    for (const release of releases) release();
    releases.clear();
  };

  return { run, dispose };
}
